'use client'

import { useState } from 'react'
import { X } from 'lucide-react'
import { Input } from './Input'

interface TagInputProps {
  id: string
  label: string
  tags: string[]
  onChange: (tags: string[]) => void
  max: number
  /** Turn raw input into a tag, or null to reject it. */
  normalize: (raw: string) => string | null
  /** What the tags are on, for the duplicate notice: "article", "discussion". */
  noun: string
  placeholder?: string
}

/**
 * A text field that turns what you type into removable tag chips.
 *
 * Enter or a comma adds; Backspace in an empty field removes the last chip;
 * leaving the field adds whatever is pending, so a tag typed and never
 * "entered" is not silently dropped on submit. Limits explain themselves
 * rather than ignoring the sixth tag.
 */
export function TagInput({ id, label, tags, onChange, max, normalize, noun, placeholder }: TagInputProps) {
  const [draft, setDraft] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  function add(raw: string) {
    const parts = raw.split(',')
    let next = tags
    let message: string | null = null
    for (const part of parts) {
      if (!part.trim()) continue
      const tag = normalize(part)
      if (!tag) {
        message = 'Tags use letters, numbers and hyphens.'
        continue
      }
      if (next.includes(tag)) {
        message = `That tag is already on this ${noun}.`
        continue
      }
      if (next.length >= max) {
        message = `${max} tags is the limit — remove one to add another.`
        break
      }
      next = [...next, tag]
    }
    if (next !== tags) onChange(next)
    setNotice(message)
    setDraft('')
  }

  function remove(tag: string) {
    onChange(tags.filter((t) => t !== tag))
    setNotice(null)
  }

  return (
    <div>
      <Input
        id={id}
        label={label}
        value={draft}
        onChange={(e) => {
          const v = e.target.value
          if (v.includes(',')) add(v)
          else setDraft(v)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add(draft)
          } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
            remove(tags[tags.length - 1])
          }
        }}
        onBlur={() => {
          if (draft.trim()) add(draft)
        }}
        placeholder={placeholder}
      />
      {notice && (
        <p role="status" className="mt-1.5 text-xs text-text-muted">
          {notice}
        </p>
      )}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-bg-subtle rounded-full text-[11px] font-medium text-text-secondary"
            >
              {tag}
              <button
                type="button"
                onClick={() => remove(tag)}
                className="hover:text-danger transition-colors"
                aria-label={`Remove tag ${tag}`}
              >
                <X className="w-3 h-3" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
